import { useLayoutEffect, useState, useRef, useCallback } from 'react'

import { useResizeObserver } from '@buffer-mono/popcorn'

export type Direction = 'horizontal' | 'vertical'
type CalculationSource = 'render' | 'resize'

function widthsMatch(a: number | undefined, b: number | undefined): boolean {
  if (a === undefined || b === undefined) return a === b
  return Math.abs(a - b) <= 0.5
}

export interface UseVisibleElementsReturn {
  containerRef: React.RefObject<HTMLDivElement>
  visibleCount: number
  hiddenCount: number
}

/**
 * Calculates how many elements fit in a container before needing an overflow indicator.
 *
 * Uses a single-container approach: all items are rendered in one container with
 * `overflow: hidden`. The hook measures children directly via `useLayoutEffect`
 * (synchronous, before paint) so the user never sees a flash.
 *
 * On the first render (when all items are present), the hook measures and caches
 * every item's dimensions. Subsequent renders with fewer items use cached data
 * for the overflow calculation, so item widths are always available even after
 * some items are removed from the DOM.
 *
 * The last child beyond `totalCount` is treated as the overflow badge element.
 * When no overflow exists, the badge should not be rendered by the consumer.
 *
 * @param options.totalCount - Number of item elements (excluding the overflow badge)
 * @param options.minimumVisibleCount - Minimum items required before calculations begin
 * @param options.direction - Layout direction ('horizontal' | 'vertical')
 * @param options.maxRows - For vertical mode: maximum rows before overflow
 */
export function useVisibleElements({
  totalCount,
  minimumVisibleCount = 0,
  direction = 'horizontal',
  maxRows,
  overflowRef,
}: {
  totalCount: number
  minimumVisibleCount?: number
  direction?: Direction
  maxRows?: number
  overflowRef?: React.RefObject<HTMLElement>
}): UseVisibleElementsReturn {
  const containerRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(totalCount)
  const [hiddenCount, setHiddenCount] = useState(0)

  // Cache measured item data from the first full render
  const cachedContainerWidth = useRef(0)
  const cachedItemWidths = useRef<number[]>([])
  const cachedVerticalRows = useRef<number[][]>([]) // row index → item indices

  const calculate = useCallback(
    (source: CalculationSource): void => {
      const container = containerRef.current
      if (!container) return

      // Skip calculation when the container hasn't been laid out yet
      // (e.g. display:none or test environment without mocked dimensions)
      if (
        container.getBoundingClientRect().width === 0 &&
        direction === 'horizontal'
      )
        return

      const containerWidth = container.getBoundingClientRect().width
      if (direction === 'vertical' && containerWidth > 0) {
        const widthChanged = !widthsMatch(
          cachedContainerWidth.current,
          containerWidth,
        )
        if (widthChanged) cachedContainerWidth.current = containerWidth
        const firstItemWidth =
          container.firstElementChild?.getBoundingClientRect().width
        const itemWidthChanged =
          firstItemWidth !== undefined &&
          cachedItemWidths.current[0] !== undefined &&
          !widthsMatch(firstItemWidth, cachedItemWidths.current[0])
        if (
          (widthChanged || itemWidthChanged) &&
          cachedVerticalRows.current.length > 0 &&
          (source === 'resize' || visibleCount === totalCount)
        ) {
          cachedItemWidths.current = []
          cachedVerticalRows.current = []
          if (source === 'resize' && visibleCount < totalCount) {
            setVisibleCount(totalCount)
            setHiddenCount(0)
            return
          }
        }
      }

      const children = Array.from(container.children).filter(
        (child): child is HTMLElement => child instanceof HTMLElement,
      )

      // On the first render all items are present (visibleCount === totalCount,
      // no badge rendered). Measure and cache their dimensions so that later
      // renders — which may only show a subset — can still compute overflow.
      // We only cache when the cache is empty (or was cleared by a totalCount
      // change) to avoid accidentally caching badge-included layouts.
      const needsCache =
        direction === 'horizontal'
          ? cachedItemWidths.current.length < totalCount
          : cachedVerticalRows.current.length === 0

      if (needsCache && children.length >= totalCount) {
        const items = children.slice(0, totalCount)
        const hasLayout = items.some(
          (el) => el.getBoundingClientRect().width > 0,
        )

        if (!hasLayout) {
          // Children have no dimensions yet — skip caching
        } else if (direction === 'horizontal') {
          cachedItemWidths.current = items.map(
            (child) => child.getBoundingClientRect().width,
          )
        } else {
          // Cache row groupings for vertical mode
          const rows: number[][] = []
          let currentRow: number[] = []
          let currentTop = items[0]?.getBoundingClientRect().top

          items.forEach((item, index) => {
            const rect = item.getBoundingClientRect()
            if (Math.abs(rect.top - currentTop) > 1) {
              if (currentRow.length > 0) {
                rows.push(currentRow)
              }
              currentRow = [index]
              currentTop = rect.top
            } else {
              currentRow.push(index)
            }
          })
          if (currentRow.length > 0) {
            rows.push(currentRow)
          }
          cachedItemWidths.current = items.map(
            (child) => child.getBoundingClientRect().width,
          )
          cachedVerticalRows.current = rows
        }
      }

      if (totalCount <= minimumVisibleCount) {
        setVisibleCount((prev) => (prev === totalCount ? prev : totalCount))
        setHiddenCount((prev) => (prev === 0 ? prev : 0))
        return
      }

      if (direction === 'vertical') {
        const renderedVisibleLimit =
          visibleCount < totalCount && maxRows && overflowRef?.current
            ? getRenderedVisibleLimit(
                children.slice(0, visibleCount),
                overflowRef.current,
                maxRows,
              )
            : undefined
        calculateVertical(
          totalCount,
          maxRows,
          cachedVerticalRows.current,
          setVisibleCount,
          setHiddenCount,
          overflowRef?.current
            ? {
                containerWidth: container.getBoundingClientRect().width,
                itemWidths: cachedItemWidths.current,
                badgeWidth: overflowRef.current.getBoundingClientRect().width,
                gap: parseFloat(getComputedStyle(container).columnGap) || 0,
              }
            : undefined,
          source === 'render' && visibleCount < totalCount
            ? Math.min(visibleCount, renderedVisibleLimit ?? visibleCount)
            : renderedVisibleLimit,
        )
      } else {
        // Detect badge: when partial render (some items hidden), badge is the
        // child right after the visible items
        const renderedItemCount = Math.min(children.length, totalCount)
        const badgeEl =
          children.length > renderedItemCount
            ? children[renderedItemCount]
            : null
        const badgeWidth = badgeEl?.getBoundingClientRect().width ?? 0

        calculateHorizontal(
          container,
          cachedItemWidths.current,
          badgeWidth,
          totalCount,
          minimumVisibleCount,
          setVisibleCount,
          setHiddenCount,
        )
      }
    },
    [
      totalCount,
      minimumVisibleCount,
      direction,
      maxRows,
      overflowRef,
      visibleCount,
    ],
  )

  // Reset cache when totalCount changes so fresh measurements happen
  const [prevTotalCount, setPrevTotalCount] = useState(totalCount)
  if (prevTotalCount !== totalCount) {
    setPrevTotalCount(totalCount)
    cachedItemWidths.current = []
    cachedVerticalRows.current = []
    setVisibleCount(totalCount)
    setHiddenCount(0)
  }

  // Measure before paint so changes to the visible items do not flash.
  useLayoutEffect(() => {
    calculate('render')
  })

  // Re-measure on container resize (e.g. window resize)
  useResizeObserver<HTMLDivElement>({
    ref: containerRef,
    onResize: () => calculate('resize'),
  })

  return {
    containerRef,
    visibleCount,
    hiddenCount,
  }
}

function getRenderedVisibleLimit(
  items: HTMLElement[],
  badge: HTMLElement,
  maxRows: number,
): number {
  const rowTops: number[] = []
  for (const [index, item] of items.entries()) {
    const top = item.getBoundingClientRect().top
    if (!rowTops.some((rowTop) => Math.abs(rowTop - top) <= 1)) {
      rowTops.push(top)
    }
    if (rowTops.length > maxRows) return index
  }

  const badgeTop = badge.getBoundingClientRect().top
  if (!rowTops.some((rowTop) => Math.abs(rowTop - badgeTop) <= 1)) {
    rowTops.push(badgeTop)
  }
  return rowTops.length > maxRows ? Math.max(0, items.length - 1) : items.length
}

function calculateVertical(
  totalCount: number,
  maxRows: number | undefined,
  cachedRows: number[][],
  setVisibleCount: React.Dispatch<React.SetStateAction<number>>,
  setHiddenCount: React.Dispatch<React.SetStateAction<number>>,
  overflow?: {
    containerWidth: number
    itemWidths: number[]
    badgeWidth: number
    gap: number
  },
  renderedVisibleLimit?: number,
): void {
  if (!maxRows || cachedRows.length === 0) return

  const visibleRows = cachedRows.slice(0, maxRows)
  let visibleItemCount = visibleRows.reduce((sum, row) => sum + row.length, 0)
  if (overflow && visibleItemCount < totalCount) {
    const lastRow = visibleRows[visibleRows.length - 1] ?? []
    let rowWidth = lastRow.reduce(
      (sum, index) => sum + (overflow.itemWidths[index] ?? 0),
      Math.max(0, lastRow.length - 1) * overflow.gap,
    )
    for (let index = lastRow.length - 1; index >= 0; index--) {
      if (
        rowWidth + overflow.gap + overflow.badgeWidth <=
        overflow.containerWidth
      )
        break
      rowWidth -= (overflow.itemWidths[lastRow[index]] ?? 0) + overflow.gap
      visibleItemCount--
    }
  }
  if (renderedVisibleLimit !== undefined) {
    visibleItemCount = Math.min(visibleItemCount, renderedVisibleLimit)
  }
  const newHidden = Math.max(0, totalCount - visibleItemCount)

  setVisibleCount((prev) =>
    prev === visibleItemCount ? prev : visibleItemCount,
  )
  setHiddenCount((prev) => (prev === newHidden ? prev : newHidden))
}

function calculateHorizontal(
  container: HTMLElement,
  itemWidths: number[],
  badgeWidth: number,
  totalCount: number,
  minimumVisibleCount: number,
  setVisibleCount: React.Dispatch<React.SetStateAction<number>>,
  setHiddenCount: React.Dispatch<React.SetStateAction<number>>,
): void {
  if (itemWidths.length === 0) return

  const containerWidth = container.getBoundingClientRect().width

  if (itemWidths.length <= minimumVisibleCount) {
    setVisibleCount((prev) => (prev === 0 ? prev : 0))
    setHiddenCount((prev) => (prev === 0 ? prev : 0))
    return
  }

  const gap = parseFloat(getComputedStyle(container).gap) || 0

  // Check if all items fit without a badge
  const totalItemsWidth = itemWidths.reduce((sum, w) => sum + w, 0)
  const totalWidthWithGaps = totalItemsWidth + (itemWidths.length - 1) * gap

  if (totalWidthWithGaps <= containerWidth) {
    setVisibleCount((prev) => (prev === totalCount ? prev : totalCount))
    setHiddenCount((prev) => (prev === 0 ? prev : 0))
    return
  }

  // Overflow detected — calculate how many items fit alongside the badge
  const availableWidth =
    badgeWidth > 0 ? containerWidth - (badgeWidth + gap) : containerWidth

  let accumulated = 0
  let count = 0

  for (let i = 0; i < itemWidths.length; i++) {
    const nextWidth = (i === 0 ? 0 : gap) + itemWidths[i]
    if (accumulated + nextWidth > availableWidth) break
    accumulated += nextWidth
    count++
  }

  const newVisible = Math.max(0, count)
  const newHidden = Math.max(0, totalCount - newVisible)

  setVisibleCount((prev) => (prev === newVisible ? prev : newVisible))
  setHiddenCount((prev) => (prev === newHidden ? prev : newHidden))
}
